/* eslint-disable react/no-danger */
/* eslint-disable react/prop-types */

import React, { useEffect, useRef, useState } from "react";
import { graphql } from "gatsby";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useShopify, useWindowDimensions } from "~hooks";
import {
  Button,
  ExpanderArea,
  Grid,
  Image,
  Layout,
  PageBuilder,
  SEO,
  SwiperCarousel
} from "~components";
import { GRID_PADDING_REM } from "~components/_common/Grid/index.jsx";
import * as PageBuilderSections from "~components/PageBuilder/sections";
import { MEDIA_QUERIES } from "~utils/css";

const GalleryFigure = styled.figure`
  width: 100%;
  height: 100%;
  position: relative;
  display: block;
`;

/** ============================================================================
 * @component
 * Core product template.
 */
const Product = ({ data, location }) => {
  // ---------------------------------------------------------------------------
  // context / ref / state

  const { addToCart, inventory } = useShopify();

  const productDetailRef = useRef();

  const [scrolled, setScrolled] = useState(false);
  const [inStock, setInStock] = useState(null);
  const [variant, setVariant] = useState(null);
  const { isDesktop } = useWindowDimensions();

  // ---------------------------------------------------------------------------
  // variables

  const cms = data.sanityShopifyProduct;

  let pageBuilderContent = {};

  if (cms?.pagebuilder) {
    pageBuilderContent = cms.pagebuilder;
  }

  // ---------------------------------------------------------------------------
  // methods

  const getCurrency = () => {
    if (!variant) {
      return ``;
    }

    return variant?.sourceData?.priceV2?.currencyCode;
  };

  const getPrice = () => {
    if (!variant) {
      return 0;
    }

    return parseFloat(variant?.sourceData?.priceV2?.amount).toFixed(2);
  };

  const scrollToDetail = () => {
    window.scrollTo({
      top: productDetailRef.current.getBoundingClientRect().height + 20,
      behavior: `smooth`
    });
  };

  // ---------------------------------------------------------------------------
  // lifecycle

  useEffect(() => {
    if (typeof window === `undefined`) {
      return () => {};
    }

    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener(`scroll`, handleScroll, false);

    return () => {
      window.removeEventListener(`scroll`, handleScroll, false);
    };
  }, []);

  useEffect(() => {
    if (cms?.variants?.[0]) {
      setVariant(cms.variants[0]);
    }
  }, [cms]);

  useEffect(() => {
    if (!variant?.id || !inventory?.[variant?.id]) {
      setInStock(false);
      return;
    }

    setInStock(inventory?.[variant?.id] === true);
  }, [inventory, variant]);

  useEffect(() => {
    if (variant) {
      window.fbq(`track`, `ViewContent`, {
        content_ids: [variant?.sourceData?.sku],
        content_name: variant?.sourceData?.title,
        content_type: `product`,
        value: variant?.sourceData?.priceV2?.amount,
        currency: variant?.sourceData?.priceV2?.currencyCode
      });
    }
  }, [variant]);

  // ---------------------------------------------------------------------------
  // render

  let expanderData = [];

  if (cms?.attributes?.[0]) {
    expanderData = {
      items: cms.attributes.map(({ heading, body }) => ({
        label: heading,
        body
      }))
    };
  }

  const galleryImages = (
    <article
      css={css`
        grid-column: span 12 / span 12;
        height: 125vw;
        top: 0;

        ${MEDIA_QUERIES?.desktop} {
          height: 100vh;
          position: sticky;
          grid-column: span 6 / span 6;
        }
      `}
    >
      <div
        css={css`
          width: 100vw;
          height: 100%;
          left: -0.75rem;
          position: relative;
          overflow: hidden;

          ${MEDIA_QUERIES?.desktop} {
            width: calc(100% + ${GRID_PADDING_REM}rem);
            left: auto;
          }
        `}
      >
        <SwiperCarousel
          _css={css`
            width: 100%;
            height: 100%;
            position: relative;
            display: block;
          `}
          slides={cms.gallery.map((galleryItem) => (
            <GalleryFigure
              key={`gallery-image-${galleryItem.asset.gatsbyImageData.images.fallback.src}`}
            >
              <Image
                _css={css`
                  width: 100%;
                  height: 100%;
                  position: relative;
                  display: block;
                  object-fit: cover;
                `}
                image={galleryItem}
              />
            </GalleryFigure>
          ))}
          nav={({ current, next, prev, to }) => (
            <>
              <div
                css={css`
                  width: 100%;
                  position: absolute;
                  bottom: 0;
                  right: 0;
                  left: 0;
                  z-index: 10;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  padding-bottom: 1rem;
                  pointer-events: none;

                  ${MEDIA_QUERIES?.desktop} {
                    padding-bottom: 1.5rem;
                  }
                `}
              >
                <p className="b3">
                  {current + 1}/{cms.gallery.length}
                </p>
              </div>
            </>
          )}
        />
      </div>
    </article>
  );

  const purchaseButtons = (
    <div
      css={css`
        width: 100%;

        overflow: hidden;
        padding: 4rem 0 2rem;
        ${MEDIA_QUERIES?.desktop} {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          z-index: 99;
          clip: rect(auto, auto, auto, auto);
          height: 100%;
          padding: 0;
        }
      `}
    >
      <div
        css={css`
          -webkit-backface-visibility: hidden;
          -moz-backface-visibility: hidden;
          backface-visibility: hidden;
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;

          ${MEDIA_QUERIES?.desktop} {
            padding: 0 0 1.5rem;
            position: fixed;
            bottom: 0;
            right: calc(50% + 1rem);
            z-index: 10;
            align-items: flex-end;
          }
        `}
      >
        <div
          css={css`
            transition: 0.3s var(--cubic-easing) opacity;

            opacity: ${scrolled ? 0 : 1};

            width: 100px;
            height: 150px;
            position: fixed;
            bottom: 1rem;
            left: -1.75rem;
            z-index: 10;
            display: none;
            align-items: center;
            justify-content: start;
            text-align: center;
            white-space: nowrap;

            ${MEDIA_QUERIES?.desktop} {
              display: flex;
            }
          `}
        >
          <div
            role="presentation"
            onClick={() => {
              scrollToDetail();
            }}
            css={css`
              transform: rotate(90deg);
              cursor: pointer;
            `}
          >
            <p className="b3">Scroll for details</p>
          </div>
        </div>

        {cms?.variants?.[1] && (
          <select
            className="text-button"
            css={css`
              height: 2.5rem;
              border-radius: 2rem;
              background: var(--color-white);
              color: var(--color-black);
              border: 1px solid var(--color-black);

              width: 100%;
              height: 2.5rem !important;
              position: relative;
              display: block;
              padding: 0 1rem;
              text-align: center;
              margin-bottom: 0.35rem;

              ${MEDIA_QUERIES?.desktop} {
                width: 200px;
                margin-bottom: 0.75rem;
              }

              ${MEDIA_QUERIES?.desktop} {
                height: 3rem;
              }
            `}
            onChange={(e) => {
              setVariant(JSON.parse(e.currentTarget.value));
            }}
            border="var(--color-black)"
            text={`$${getPrice()} ${getCurrency()}`}
          >
            {cms.variants.map((selectableVariant) => (
              <option
                key={`pdp-select-${selectableVariant.id}`}
                value={JSON.stringify(selectableVariant)}
              >
                {selectableVariant.title}
              </option>
            ))}
          </select>
        )}

        <Button
          _css={css`
            width: 100%;
            height: 2.5rem !important;
            position: relative;
            display: block;
            margin-bottom: 0.35rem;
            pointer-events: none;

            ${MEDIA_QUERIES?.desktop} {
              width: 200px;
              margin-bottom: 0.75rem;
            }
          `}
          border="var(--color-black)"
          text={`$${getPrice()} ${getCurrency()}`}
        />

        <Button
          _css={css`
            opacity: ${inStock ? 1 : 0.5};
            pointer-events: ${inStock ? `auto` : `none`};

            width: 100%;
            height: 2.5rem !important;
            position: relative;
            display: block;

            ${MEDIA_QUERIES?.desktop} {
              width: 200px;
            }
          `}
          border="var(--color-black)"
          onClick={() => {
            if (inStock) {
              addToCart(variant?.id);

              window.fbq(`track`, `AddToCart`, {
                content_ids: [variant?.sourceData?.sku],
                content_name: variant?.sourceData?.title,
                content_type: `product`,
                value: variant?.sourceData?.priceV2?.amount,
                currency: variant?.sourceData?.priceV2?.currencyCode
              });
            }
          }}
          text={inStock ? `Add to Bag` : `Out of Stock`}
        />
      </div>
    </div>
  );

  return (
    <>
      <SEO
        customTitle={cms?.title}
        customDescription={cms?.seoDescription}
        customKeywords={cms?.seoKeywords}
        path={location.pathname}
      />

      <Layout>
        <section>
          <Grid>
            {!isDesktop && galleryImages}

            <article
              css={css`
                grid-column: span 12 / span 12;
                position: relative;
                z-index: 10;
                background: white;
                overflow: hidden;

                ${MEDIA_QUERIES?.desktop} {
                  grid-column: span 6 / span 6;
                }
              `}
            >
              {isDesktop && purchaseButtons}

              <div
                css={css`
                  position: relative;

                  ${MEDIA_QUERIES?.desktop} {
                    height: 100vh;
                    padding: 6rem 0 1rem;
                  }
                `}
              >
                <h1
                  css={css`
                    margin-top: 0.5rem;
                    text-transform: uppercase;
                    text-align: center;
                    line-height: 24px;

                    ${MEDIA_QUERIES?.desktop} {
                      margin-top: 0;
                      line-height: 38px;
                    }
                  `}
                  className="h1"
                >
                  {cms?.title}
                </h1>

                <p
                  css={css`
                    text-align: left;
                  `}
                  className="b1"
                >
                  <span
                    dangerouslySetInnerHTML={{ __html: cms?.description }}
                  />
                </p>
              </div>

              {!isDesktop && purchaseButtons}

              {(isDesktop && (
                <div
                  ref={productDetailRef}
                  css={css`
                    position: relative;

                    ${MEDIA_QUERIES?.desktop} {
                      padding: 6rem 0 0;
                    }
                  `}
                >
                  {cms?.attributes?.[0] && (
                    <ul>
                      {cms.attributes.map((attribute, attributeIndex) => (
                        <li
                          key={`product-attribute-${attribute.heading
                            .toLowerCase()
                            .replace(` `, `-`)}`}
                          css={css`
                            position: relative;

                            margin-bottom: ${attributeIndex <
                            cms?.attributes?.length - 1
                              ? `3rem`
                              : `1.5rem`};
                          `}
                        >
                          <h3
                            css={css`
                              text-align: center;
                            `}
                            className="h4"
                          >
                            {attribute.heading}
                          </h3>

                          <div
                            className="b3"
                            css={css`
                              white-space: pre-wrap;
                            `}
                            dangerouslySetInnerHTML={{ __html: attribute.body }}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )) || (
                <>
                  {expanderData?.items?.[0] && (
                    <ExpanderArea data={expanderData} />
                  )}
                </>
              )}
            </article>

            {isDesktop && galleryImages}
          </Grid>
        </section>

        <PageBuilder
          content={pageBuilderContent}
          sections={PageBuilderSections}
        />
      </Layout>
    </>
  );
};

export default Product;

export const query = graphql`
  query ProductPage($id: String!) {
    sanityShopifyProduct(id: { eq: $id }) {
      ...ProductBuilderFragment

      title
      handle
      description
      seoDescription
      seoKeywords

      attributes {
        heading
        body
      }

      gallery {
        asset {
          gatsbyImageData(
            width: 2048
            placeholder: BLURRED
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }

      sourceData {
        id
        title
        handle
        vendor
        description

        images {
          edges {
            node {
              originalSrc
            }
          }
        }
      }

      variants {
        id
        title

        sourceData {
          availableForSale
          id
          sku
          title

          priceV2 {
            amount
            currencyCode
          }

          selectedOptions {
            name
            value
          }
        }
      }
    }
  }
`;
